import { OrderType } from '../../../types/orders';

export const tableIds = {
  DUPLICATE_COMMUNICATION: 'duplicate-communication',
  WORKORDER_COMMUNICATIONS: 'workorders-communications',
  SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS:
    'search-and-create-historical-communications',
  WORKORDER_GENERAL_VIEW: 'workorders-general-view',
  DUPLICATE_GENERIC_ORDER: 'duplicate-generic-order',
  SEARCH_AND_CREATE_HISTORICAL_WORKORDERS:
    'search-and-create-historical-workorders',
  SEARCH_AND_CREATE_HISTORICAL_ACTIONS: 'search-and-create-historical-actions',
  SEARCH_AND_CREATE_HISTORICAL_BUDGETS: 'search-and-create-historical-budgets',
  ACCIDENTS: 'accidents',
  CAMPAIGNS: 'campaigns',
  SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS:
    'search-and-create-historical-campaigns',
  MULTIMEDIA: 'multimedia',
  GENERIC_ORDER_MODAL: 'generic-order-modal',
  CAMPAIGN_MODAL: 'campaign-modal',
  VALIDATION_ACTIONS: 'validation-actions',
  SURVEILLANCE_ROUTES: 'surveillance-routes',
  SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES:
    'search-and-create-historical-surveillance-routes',
  GENERAL_ACTIONS: 'general-actions',
  REPORTING_ACTIONS: 'reporting-historical-actions',
  REPORTING_ACCIDENTS: 'reporting-historical-accidents',
  DASHBOARD_ACTIVE_ORDERS: 'dashboard-active-orders',
  REPORTING_TIME_MANAGEMENT: 'reporting-time-management',
  // P15 rework #3 (2026-07-09) — modal "Vincular amb tram de recorregut"
  // (`RelateSegmentModal`). Cursor Redux del `InputRadioTagTable` reusado TAL
  // CUAL por ese modal (NO una tabla hermana nueva — los 2 intentos previos
  // que sí creaban una fueron rechazados por el usuario). Solo se usa el
  // rango de fechas (`fromDate`/`toDate`); el resto de campos del cursor
  // quedan sin efecto porque sus filtros correspondientes están ocultos.
  SEGMENT_MODAL: 'segment-modal',
  // Cross-link 3 (2026-09-22): modales de vínculo recorrido ↔ OT. Cursor
  // propio por modal (mismo criterio que SEGMENT_MODAL).
  ORDER_LINK_MODAL: 'order-link-modal',
  ROUTE_LINK_MODAL: 'route-link-modal',
  // Transfer básica→RV (2026-07-21) — tabla de RVs destino del
  // `TransferSubOrderModal` en modo RV (reuso de `InputRadioTagTable`, patrón
  // SEGMENT_MODAL/GENERIC_ORDER_MODAL). Cursor propio para no pisar los
  // filtros persistidos del listado RV (`SURVEILLANCE_ROUTES`).
  SURVEILLANCE_ROUTE_MODAL: 'surveillance-route-modal',
  // «Transferir tramo» (14-oct): destinos del tramo en la misma tabla de
  // selección (`InputRadioTagTable`). Cursor propio, como los demás modales.
  SEGMENT_TRANSFER_MODAL: 'segment-transfer-modal',
  MOVE_SEGMENTS_MODAL: 'move-segments-modal',
  // Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — pestañas del LISTADO
  // del módulo RV (`GeneralViewRoutePage`, `/surveillance-route`), NO del
  // detalle de cada recorrido (diseño corregido por el usuario tras
  // screenshots: las 2 tabs del detalle — Vista prèvia + Órdenes asociadas —
  // no cambian). Filtran por TIPO de origen (`containerTypes:
  // ['surveillanceRoute']`), no por un `containerId` concreto — semántica
  // "todas las actuaciones/multimedia de recorridos de vigilancia", espejo
  // de CAMPAIGN_LIST_ACTIONS/CAMPAIGN_LIST_MULTIMEDIA de abajo.
  // Model-aware: solo Actuaciones y Multimedia (el RV NO
  // admite Accidentes ni Comunicaciones como hijas, hierarchy-validator.ts:
  // 24-25) — sin homólogos RV_ACCIDENTS/RV_COMMUNICATIONS por diseño.
  RV_ACTIONS: 'rv-actions',
  RV_MULTIMEDIA: 'rv-multimedia',
  // Pestañas de módulo del LISTADO de Campañas (`/work-orders/campaign`,
  // `GeneralViewCampaignPage`) — espejo EXACTO del patrón RV_ACTIONS/
  // RV_MULTIMEDIA de arriba, pero con las 4 entidades que sí admite una
  // Campaña (a diferencia del RV, que solo admite Actuaciones/Multimedia).
  // Filtran por TIPO de origen (`containerTypes: ['campaign']`), no por un
  // `containerId` concreto — "todas las actuaciones/accidentes/
  // comunicaciones/multimedia de campañas", no las de UNA campaña (para eso
  // se entra en el detalle, cuyo ShowOT ya lista sus hijas).
  CAMPAIGN_LIST_ACTIONS: 'campaign-list-actions',
  CAMPAIGN_LIST_ACCIDENTS: 'campaign-list-accidents',
  CAMPAIGN_LIST_COMMUNICATIONS: 'campaign-list-communications',
  CAMPAIGN_LIST_MULTIMEDIA: 'campaign-list-multimedia',
};

// Fase 4 nav Trabajo/Revisión (2026-07-12) — tableIds de las pestañas
// CONTEXTUALES (campaña/RV) de las 4 tablas de entidad. La columna+filtro
// "Origen" (containerType) es redundante ahí — la página ya está fijada a un
// container concreto (`containerId`) o a un TIPO fijo (`containerTypes`
// estático de RV) — se deriva del `tableId` en vez de threadear una prop
// nueva por las ~4 páginas (menor superficie de cambio, mismo criterio que
// `TABLE_ENTITY_TYPES` de arriba).
export const CONTAINER_SCOPED_TABLE_IDS: ReadonlySet<string> = new Set([
  tableIds.RV_ACTIONS,
  tableIds.RV_MULTIMEDIA,
  tableIds.CAMPAIGN_LIST_ACTIONS,
  tableIds.CAMPAIGN_LIST_ACCIDENTS,
  tableIds.CAMPAIGN_LIST_COMMUNICATIONS,
  tableIds.CAMPAIGN_LIST_MULTIMEDIA,
]);

/**
 * Mapeo de tableId → tipo(s) de entidad que muestra la tabla.
 * Usado para filtrar opciones del dropdown Estado por entidad.
 *
 * - Singular (`OrderType`): tabla mono-tipo (la mayoría).
 * - Array (`OrderType[]`): vista multi-entidad — el dropdown muestra
 *   la UNIÓN de los estados aplicables a cualquiera de los tipos.
 *   Caso actual: MULTIMEDIA agrega Actions + Accidents en la misma tabla.
 *
 * @see BUG #29 — getStateOptionsByRoleAndEntityType usa este mapping
 * @see docs/superpowers/specs/2026-05-21-multimedia-state-filter-union-design.md
 */
export const TABLE_ENTITY_TYPES: Partial<Record<string, OrderType | OrderType[]>> = {
  [tableIds.WORKORDER_GENERAL_VIEW]: OrderType.Generic,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS]: OrderType.Generic,
  [tableIds.DUPLICATE_GENERIC_ORDER]: OrderType.Generic,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS]: OrderType.Communication,
  [tableIds.WORKORDER_COMMUNICATIONS]: OrderType.Communication,
  [tableIds.DUPLICATE_COMMUNICATION]: OrderType.Communication,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_ACTIONS]: OrderType.Action,
  [tableIds.GENERAL_ACTIONS]: OrderType.Action,
  [tableIds.VALIDATION_ACTIONS]: OrderType.Action,
  [tableIds.ACCIDENTS]: OrderType.Accident,
  [tableIds.CAMPAIGNS]: OrderType.Campaign,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS]: OrderType.Campaign,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS]: OrderType.Action,
  // GAP-2 paridad RV (2026-07-17): sin estas entradas el dropdown Estado del
  // RV mostraba la lista genérica por rol (managed/pending-work/validated/…
  // imposibles en un RV) y NO ofrecía in-progress/paused (los suyos, D5).
  [tableIds.SURVEILLANCE_ROUTES]: OrderType.SurveillanceRoute,
  [tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES]:
    OrderType.SurveillanceRoute,
  // Multi-entidad: Multimedia agrega Actions + Accidents.
  // Para añadir un 3º tipo (e.g. SurveillanceRoute), basta con extender el array.
  [tableIds.MULTIMEDIA]: [OrderType.Action, OrderType.Accident],
  [tableIds.REPORTING_ACTIONS]: OrderType.Action,
  [tableIds.REPORTING_ACCIDENTS]: OrderType.Accident,
  [tableIds.GENERIC_ORDER_MODAL]: OrderType.Generic,
  [tableIds.CAMPAIGN_MODAL]: OrderType.Campaign,
  // Vincular una OT al recorrido o a un aviso (2026-09-24): lista genéricas Y
  // campañas — sin esta entrada el filtro Estado no tenía opciones. Desde el
  // 2026-10-28 van en dos pestañas que COMPARTEN este cursor (los filtros
  // valen para las dos), así que sigue siendo la unión.
  [tableIds.ORDER_LINK_MODAL]: [OrderType.Generic, OrderType.Campaign],
  // Transfer básica→RV: la tabla del modal es dominio RV — esta entrada es la
  // que activa `isSurveillanceTable` en InputRadioTagTable (columnas/headers
  // ordenables propios del RV, p.ej. derivedEndDate→registerEndDate).
  [tableIds.SURVEILLANCE_ROUTE_MODAL]: OrderType.SurveillanceRoute,
  // Revisión del 22-oct: las tablas de destinos de «Transferir tramo» y
  // «Mover tramos» son de recorridos (su «Fecha final» se ordena).
  [tableIds.SEGMENT_TRANSFER_MODAL]: OrderType.SurveillanceRoute,
  [tableIds.MOVE_SEGMENTS_MODAL]: OrderType.SurveillanceRoute,
  // Fase 3 nav Trabajo/Revisión — RV_MULTIMEDIA usa OrderType.Action singular
  // (no array): a diferencia de MULTIMEDIA/CAMPAIGN_LIST_MULTIMEDIA (Action +
  // Accident), el RV solo puede tener Actuaciones como hijas — nunca
  // Accidents (hierarchy-validator.ts:24-25) — no hay unión de tipos.
  [tableIds.RV_ACTIONS]: OrderType.Action,
  [tableIds.RV_MULTIMEDIA]: OrderType.Action,
  // Pestañas de módulo del LISTADO de Campañas — mismo tipo de entidad que
  // su homólogo global (mismo criterio que RV_ACTIONS arriba).
  [tableIds.CAMPAIGN_LIST_ACTIONS]: OrderType.Action,
  [tableIds.CAMPAIGN_LIST_ACCIDENTS]: OrderType.Accident,
  [tableIds.CAMPAIGN_LIST_COMMUNICATIONS]: OrderType.Communication,
  [tableIds.CAMPAIGN_LIST_MULTIMEDIA]: [OrderType.Action, OrderType.Accident],
};

// Filtro de Estado (2026-08-20) — qué listados recortan las OPCIONES por rol.
//
// La regla, en una frase: **el desplegable de Estado se interseca por rol solo
// donde el listado que lo alimenta se interseca por rol**. Recortar de más
// esconde filas que SÍ llegan (el operario las ve en la tabla y no puede
// filtrarlas); recortar de menos regala opciones que devuelven 0 filas siempre.
//
// Quién recorta hoy en el backend, y con qué mapa:
//   - `generic-orders.service`     → ROLE_VISIBLE_STATES
//   - `orders.service`             → ROLE_VISIBLE_STATES      (alimenta Multimedia)
//   - `surveillance-route.service` → SR_ROLE_VISIBLE_STATES
// Y quién NO: `actions`, `accidents`, `communication`, `campaigns` — sus
// services ni reciben el rol (verificado 2026-08-20: 0 referencias a
// `getVisibleStatesForRole`, y `actions.findAll` no tiene el parámetro).
//
// El bug que lo motiva: `ROLE_VISIBLE_STATES[Tablet]` está escrito en el
// vocabulario de la OT PADRE (`pending-work`/`work-in-progress`/
// `pending-validation`) y se aplicaba también a las hijas, cuya máquina de
// estados no comparte ni una palabra con esa — la intersección se quedaba en
// `open` y el Tablet no podía filtrar por «Anulada» una tabla que le estaba
// pintando filas anuladas. Es el mismo desajuste que la PR #233 cerró en el
// «Cambiar estado»; esto es su gemelo en el listado.
//
// Se deriva del `tableId` en vez de threadear una prop nueva por las páginas
// (mismo criterio que `TABLE_ENTITY_TYPES` y `CONTAINER_SCOPED_TABLE_IDS`).
//
// Tripwire: `tests/unit/rbac/state-filter-options.spec.ts`.
export const ROLE_FILTERED_LISTING_TABLE_IDS: ReadonlySet<string> = new Set([
  // → `genericOrders`
  tableIds.WORKORDER_GENERAL_VIEW,
  tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS,
  tableIds.DUPLICATE_GENERIC_ORDER,
  tableIds.GENERIC_ORDER_MODAL,
  // → `surveillanceRoutes`
  tableIds.SURVEILLANCE_ROUTES,
  tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES,
  tableIds.SURVEILLANCE_ROUTE_MODAL,
  // Las 3 tablas Multimedia NO están aquí: van por `orders`, que desde
  // rois-back#110 recorta por rol **según el tipo de cada fila** y deja libres
  // a las hijas (actuación, accidente, tramo) — que es justo lo que Multimedia
  // lista. Mantenerlas dentro reproducía el bug original al otro lado:
  // verificado en navegador el 2026-08-21 contra el backend con ese arreglo,
  // la tabla enseñaba filas «Validada» y el desplegable seguía ofreciendo solo
  // «Abierta».
]);

/**
 * ¿El listado de esta tabla recorta sus filas por rol en el backend? Si no, su
 * filtro de Estado debe ofrecer TODOS los estados válidos de la entidad.
 */
export const listingFiltersByRole = (tableId?: string | null): boolean =>
  !!tableId && ROLE_FILTERED_LISTING_TABLE_IDS.has(tableId);

// Tarea #11 (post-review): subido de 13 a 25 para alinear con DESKTOP_DEFAULT
// del page-size selector. Antes el fallback `cursor.take ?? takeBase` daba 13
// y el selector mostraba 25 → mismatch cosmético en primera carga. Ahora
// coincide. Decisión UX: el scroll en cualquier viewport es aceptable,
// por eso tablet también usa 25 (no se diferencia más por viewport).
export const takeBase = 25;

export const takeModal = 8;

// Transfer básica→RV (2026-07-21): el modal RV pide takeModal+1 para absorber
// la fila del RV padre actual, que se excluye client-side de la lista de
// destinos ("transferir a OTRO RV") — así la página sigue mostrando ~8 filas.
export const takeModalSurveillanceRoute = takeModal + 1;

export const takeModalTablet = 5;

export type DataCursorArrowTables =
  | {
      take: number;
      cursor: string;
      orderBy: string;
      roadId?: string | undefined;
      concessionId?: string | undefined;
      interlocutorGroupId?: string | undefined;
      fromDate?: string | undefined;
      toDate?: string | undefined;
      indexType?: number | undefined;
      indexExpedition?: number | undefined;
      classification?: string | undefined;
      state?: string | undefined;
      capitolId?: string | undefined;
      subCapitolId?: string | undefined;
      mediumKnowledgeId?: string | undefined;
      weatherId?: string | undefined;
      name?: string | undefined;
      type?: string | undefined;
      // Reporting/Time Management — filtros propios del informe agregado.
      // Aditivo y opcional, no afecta a consumidores existentes.
      teamIds?: string[] | undefined;
      userIds?: string[] | undefined;
      externalCompanyIds?: string[] | undefined;
      states?: string[] | undefined;
      // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" en las 4
      // tablas globales de entidad. Espejo del `containerTypes` estático que
      // ya usan las pestañas contextuales RV (Fase 3) — aquí es user-driven.
      containerTypes?: string[] | undefined;
    }
  | undefined;

export type pageInfoType = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};
